// The window last computed from an account's full history, per user, stored by
// unit id so the first launch after a content release (or with the remote pull
// pending) waits on the right unit and keeps the right recordings: a release
// that inserts or removes units shuffles the indices, not the unit ids.

import type { AudioWindow } from "./audio-sets.ts";

const key = (userId: string) => `aybuben:audio-window:${userId}`;

export function loadWindow(
	userId: string,
	units: readonly { id: string }[],
	storage: Pick<Storage, "getItem"> | null = globalThis.localStorage ?? null,
): AudioWindow | null {
	try {
		const v: unknown = JSON.parse(storage?.getItem(key(userId)) ?? "null");
		if (typeof v !== "object" || v === null || Array.isArray(v)) return null;
		const { current, last } = v as { current?: unknown; last?: unknown }; // a non-null object, checked above
		// Entries written before this switched to ids hold indices (numbers); an old
		// index may point at the wrong unit, so drop them instead of converting.
		if (typeof current !== "string" || typeof last !== "string") return null;
		const ci = units.findIndex((u) => u.id === current);
		const li = units.findIndex((u) => u.id === last);
		return ci >= 0 && li >= 0 ? { current: ci, last: li } : null;
	} catch {
		return null;
	}
}

export function saveWindow(
	userId: string,
	w: AudioWindow,
	units: readonly { id: string }[],
	storage: Pick<Storage, "setItem"> | null = globalThis.localStorage ?? null,
): void {
	const current = units[w.current]?.id;
	const last = units[w.last]?.id;
	if (current === undefined || last === undefined) return; // outside the course: nothing to map
	try {
		storage?.setItem(key(userId), JSON.stringify({ current, last }));
	} catch {
		/* storage blocked or full: the next launch just has no hint */
	}
}
